import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { BadgeCheck, Check, Crown, Sparkles, Infinity } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { StripeEmbeddedMembershipCheckout } from "@/components/site/StripeEmbeddedMembershipCheckout";
import type { MembershipPriceId } from "@/lib/membership";

export const Route = createFileRoute("/membership")({
  head: () => ({
    meta: [
      { title: "KAfrequency Membership · Buddha KA" },
      { name: "description", content: "Compare Free, KAfrequency Membership, Inner Circle, and Lifetime Ascension Pass tiers. Monthly and annual billing available." },
      { property: "og:title", content: "KAfrequency Membership" },
      { property: "og:description", content: "Choose the KAfrequency access tier that fits your path." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://buddhaka.com/membership" },
      { property: "og:site_name", content: "Buddha KA · KAfrequency" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "KAfrequency Membership" },
      { name: "twitter:description", content: "Choose the tier that fits your path." },
    ],
    links: [{ rel: "canonical", href: "https://buddhaka.com/membership" }],
  }),
  component: MembershipPage,
});

type Cadence = "monthly" | "annual";

type Plan = {
  name: string;
  monthly: { price: string; priceId: MembershipPriceId };
  annual?: { price: string; priceId: MembershipPriceId; savings: string };
  badge?: string;
  icon: React.ReactNode;
  features: string[];
};

const freePlan = {
  name: "Free Account",
  price: "$0",
  cadence: "forever",
  icon: <BadgeCheck size={18} aria-hidden />,
  features: ["Account dashboard", "Purchase history", "Digital library", "Wishlist", "Free music and selected content", "Public blog and announcements"],
};

const paidPlans: Plan[] = [
  {
    name: "KAfrequency Membership",
    monthly: { price: "$14.99", priceId: "kafrequency_membership_monthly" },
    annual: { price: "$149.00", priceId: "kafrequency_membership_annual", savings: "Save ~17%" },
    badge: "Recommended",
    icon: <Sparkles size={18} aria-hidden />,
    features: ["Everything in Free", "Early access to new music", "Weekly exclusive songs and demos", "Daily KAfrequency Activations", "Members-only teachings", "Exclusive livestreams", "Private community", "10% merchandise discount", "Member badge"],
  },
  {
    name: "Inner Circle",
    monthly: { price: "$49.99", priceId: "kafrequency_inner_circle_monthly" },
    annual: { price: "$499.00", priceId: "kafrequency_inner_circle_annual", savings: "Save ~17%" },
    icon: <Crown size={18} aria-hidden />,
    features: ["Everything in Membership", "Exclusive unreleased music", "Monthly live Q&A", "Behind-the-scenes studio sessions", "Premium meditation library", "Early book and project access", "Priority support", "20% merchandise discount", "VIP badge"],
  },
];

const lifetimePlan = {
  name: "Lifetime Ascension Pass",
  price: "$499.99",
  cadence: "one-time",
  icon: <Infinity size={18} aria-hidden />,
  priceId: "lifetime_ascension_pass" as MembershipPriceId,
  features: ["Lifetime premium access", "Lifetime daily activations", "Premium meditation library", "Lifetime member badge", "Priority future digital releases", "Annual gifts and bonuses", "Permanent merchandise discount", "Founder-level recognition"],
};

function MembershipPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [cadence, setCadence] = useState<Cadence>("monthly");
  const [checkout, setCheckout] = useState<MembershipPriceId | null>(null);

  useEffect(() => {
    track("membership_page_view");
    supabase.auth.getUser().then(({ data }) => setAuthed(!!data.user));
  }, []);

  const handleChoose = (priceId: MembershipPriceId) => {
    if (authed === false) {
      track("signup_start", { source: "membership", priceId });
      navigate({ to: "/auth" });
      return;
    }
    track("membership_plan_select", { priceId });
    setCheckout((current) => (current === priceId ? null : priceId));
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">KAfrequency Membership</p>
        <h1 className="mt-4 font-serif text-5xl sm:text-6xl">Choose your frequency of access.</h1>
        <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
          Start free, join monthly or annually for the best value, enter the Inner Circle for deeper access, or claim lifetime premium access.
        </p>
      </div>

      <div className="mt-10 inline-flex rounded-full border hairline bg-charcoal p-1" role="tablist" aria-label="Billing cadence">
        {(["monthly", "annual"] as const).map((c) => (
          <button
            key={c}
            role="tab"
            aria-selected={cadence === c}
            onClick={() => setCadence(c)}
            className={`rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition ${cadence === c ? "bg-gold-gradient text-obsidian" : "text-muted-foreground hover:text-ivory"}`}
          >
            {c === "monthly" ? "Monthly" : "Annual · Save"}
          </button>
        ))}
      </div>

      <section className="mt-8 grid gap-4 lg:grid-cols-4">
        {/* Free */}
        <article className="relative rounded-xl border hairline bg-charcoal p-6">
          <div className="text-gold">{freePlan.icon}</div>
          <h2 className="mt-4 font-serif text-2xl">{freePlan.name}</h2>
          <p className="mt-4 font-serif text-4xl text-gold-luminous">{freePlan.price}</p>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{freePlan.cadence}</p>
          <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
            {freePlan.features.map((f) => (
              <li key={f} className="flex gap-2"><Check className="mt-0.5 shrink-0 text-gold" size={14} aria-hidden /> {f}</li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => navigate({ to: "/auth" })}
            className="mt-7 w-full rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian transition hover:opacity-90"
          >
            Create free account
          </button>
        </article>

        {/* Paid recurring */}
        {paidPlans.map((plan) => {
          const active = cadence === "annual" && plan.annual ? plan.annual : plan.monthly;
          return (
            <article key={plan.name} className={`relative rounded-xl border hairline bg-charcoal p-6 ${plan.badge ? "border-gold/60" : ""}`}>
              {plan.badge && (
                <span className="absolute right-4 top-4 rounded-full bg-gold-gradient px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-obsidian">
                  {plan.badge}
                </span>
              )}
              <div className="text-gold">{plan.icon}</div>
              <h2 className="mt-4 font-serif text-2xl">{plan.name}</h2>
              <p className="mt-4 font-serif text-4xl text-gold-luminous">{active.price}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {cadence === "annual" ? "billed yearly" : "billed monthly"}
                {cadence === "annual" && plan.annual && <span className="ml-2 text-gold">· {plan.annual.savings}</span>}
              </p>
              <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
                {plan.features.map((f) => (
                  <li key={f} className="flex gap-2"><Check className="mt-0.5 shrink-0 text-gold" size={14} aria-hidden /> {f}</li>
                ))}
              </ul>
              <button
                type="button"
                disabled={authed === null}
                onClick={() => handleChoose(active.priceId)}
                className="mt-7 w-full rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian transition hover:opacity-90 disabled:opacity-50"
              >
                {checkout === active.priceId ? "Close checkout" : "Choose plan"}
              </button>
            </article>
          );
        })}

        {/* Lifetime */}
        <article className="relative rounded-xl border hairline bg-charcoal p-6">
          <div className="text-gold">{lifetimePlan.icon}</div>
          <h2 className="mt-4 font-serif text-2xl">{lifetimePlan.name}</h2>
          <p className="mt-4 font-serif text-4xl text-gold-luminous">{lifetimePlan.price}</p>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{lifetimePlan.cadence}</p>
          <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
            {lifetimePlan.features.map((f) => (
              <li key={f} className="flex gap-2"><Check className="mt-0.5 shrink-0 text-gold" size={14} aria-hidden /> {f}</li>
            ))}
          </ul>
          <button
            type="button"
            disabled={authed === null}
            onClick={() => handleChoose(lifetimePlan.priceId)}
            className="mt-7 w-full rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian transition hover:opacity-90 disabled:opacity-50"
          >
            {checkout === lifetimePlan.priceId ? "Close checkout" : "Choose plan"}
          </button>
        </article>
      </section>

      {checkout && (
        <section className="mt-10">
          <StripeEmbeddedMembershipCheckout priceId={checkout} />
        </section>
      )}
    </div>
  );
}
